/**
 * ============================================================================
 * [P0-01] Firebase Configuration Service
 *
 * Firebase 초기화 및 서비스 인스턴스 제공
 *
 * 환경 변수:
 * - VITE_FIREBASE_API_KEY
 * - VITE_FIREBASE_AUTH_DOMAIN
 * - VITE_FIREBASE_PROJECT_ID
 * - VITE_FIREBASE_STORAGE_BUCKET
 * - VITE_FIREBASE_MESSAGING_SENDER_ID
 * - VITE_FIREBASE_APP_ID
 * ============================================================================
 */

import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getAuth, type Auth } from 'firebase/auth'
import { getFirestore, type Firestore } from 'firebase/firestore'
import { getStorage, type FirebaseStorage } from 'firebase/storage'

// ============================================================================
// Firebase 설정 객체
// - 환경 변수에서 값을 로드 (보안)
// ============================================================================
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

// ============================================================================
// 환경 변수 검증
// - 필수 환경 변수가 설정되지 않으면 경고 출력
// ============================================================================
const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
] as const

for (const envVar of requiredEnvVars) {
  if (!import.meta.env[envVar]) {
    console.warn(`[Firebase] Missing environment variable: ${envVar}`)
  }
}

// ============================================================================
// Firebase 앱 초기화
// ============================================================================
const app: FirebaseApp = initializeApp(firebaseConfig)

// ============================================================================
// Firebase 서비스 인스턴스
// ============================================================================
export const auth: Auth = getAuth(app)
export const db: Firestore = getFirestore(app)
export const storage: FirebaseStorage = getStorage(app)

export default app
